package com.fantasytenis.shared.core.network

import kotlinx.serialization.Serializable

/**
 * Sealed class representing API response states.
 */
sealed class ApiResponse<out T> {
    data class Success<T>(val data: T) : ApiResponse<T>()
    data class Error(val message: String, val code: Int = 0) : ApiResponse<Nothing>()

    val isSuccess: Boolean get() = this is Success
    val isError: Boolean get() = this is Error

    fun getOrNull(): T? = (this as? Success)?.data
    fun errorOrNull(): String? = (this as? Error)?.message
}

/**
 * Standard API response wrapper.
 * Matches the backend response format: { status, error, data }
 * Note: status defaults to 0 to handle proto3 default value omission
 */
@Serializable
data class WrappedResponse<T>(
    val status: Int = 0,
    val error: ApiError? = null,
    val data: T? = null
) {
    val isSuccess: Boolean get() = error == null && status in 200..299
}

/**
 * Standard API error structure.
 */
@Serializable
data class ApiError(
    val type: String,
    val message: String
)

/**
 * Paginated response wrapper.
 */
@Serializable
data class PaginatedResponse<T>(
    val items: List<T>,
    val total: Int,
    val page: Int,
    val limit: Int,
    val totalPages: Int
)

/**
 * Extension to extract data or throw exception.
 */
fun <T> WrappedResponse<T>.getOrThrow(): T {
    if (error != null) {
        throw ApiException(error.type, error.message, status)
    }
    return data ?: throw ApiException("NO_DATA", "Response contained no data", status)
}

/**
 * API Exception with error details.
 */
class ApiException(
    val type: String,
    override val message: String,
    val status: Int
) : Exception(message)

/**
 * Generic error response for parsing error responses where the data field
 * might be null, an empty array [], or any other JSON value.
 * The data field is omitted to allow kotlinx.serialization to ignore it.
 */
@Serializable
data class GenericErrorResponse(
    val status: Int = 0,
    val error: String? = null
) {
    val isSuccess: Boolean get() = status in 200..299 && (error == null || error.isBlank())
}

/**
 * JSON instance configured for lenient parsing (ignores unknown keys).
 */
val lenientJson = kotlinx.serialization.json.Json { ignoreUnknownKeys = true }

/**
 * Parse response body checking for errors first.
 * Backend returns HTTP 200 even for errors, with actual status in body.
 * This avoids the data:[] parsing issue by checking error status first.
 */
inline fun <reified T> parseResponseWithErrorCheck(
    bodyText: String,
    defaultError: String
): ApiResponse<T> {
    // First parse just status/error to check for errors
    val errorCheck = lenientJson.decodeFromString<GenericErrorResponse>(bodyText)

    return if (errorCheck.isSuccess) {
        // Success - parse full response with data
        try {
            val data = lenientJson.decodeFromString<T>(bodyText)
            ApiResponse.Success(data)
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: defaultError, errorCheck.status)
        }
    } else {
        // Error response - use the error from errorCheck
        ApiResponse.Error(errorCheck.error ?: defaultError, errorCheck.status)
    }
}
